import { useEffect, useState } from 'react';
import { Button, Checkbox, Dropdown, Field, Header, Icon, Input, Label, Message, Modal, Table } from 'react-frontier';
import { PromoterActivity, PromoterActivityType, SetLoading } from '../SiteClasses';
import { bindClick, formatUnix, nowUnix, nowDatetimeInput, datetimeInputToUnix, activityStatus } from '../SiteUtil';
import { useUser } from '../Hooks';
import UserAccess from '../UserAccess';
import Validator from '../Validator';
import Toast from '../Toast';
import API from '../API';

enum Modals{
	CREATE = 1,
	DELETE = 2,
	INFO = 3,
	EDIT = 4,
}

interface ActivitiesProps{
	prospect_id?: number,
	promoter_id?: number,
	include_prospect_id?: number,
}

interface ActivityForm{
	activity_type_id: number,
	title: string,
	description: string,
	outcome: string,
	date_activity: string,
	completed: boolean,
}

var Activities = (props: ActivitiesProps)=>{
	var { user, hasAccess } = useUser();
	var canEdit = hasAccess(props.prospect_id ? UserAccess.PROSPECTS.EDIT_PROSPECTS : UserAccess.PROMOTERS.EDIT_PROMOTERS);
	var target = props.prospect_id ? { prospect_id: props.prospect_id } : { promoter_id: props.promoter_id };
	var targetKey = props.prospect_id || props.promoter_id;
	var [activities, setActivities] = useState<PromoterActivity[]>(null);
	var [types, setTypes] = useState<PromoterActivityType[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [selected, setSelected] = useState<PromoterActivity>(null);
	var [form, setForm] = useState<ActivityForm>(null);
	var [editingId, setEditingId] = useState<number>(null);
	var [prompts, setPrompts] = useState<string[]>(null);

	useEffect(()=>{ get(); }, [targetKey, props.include_prospect_id]);

	var get = ()=>{
		setLoadError(null);
		API.getActivities({ ...target, include_prospect_id: props.include_prospect_id }).then(res=>{
			if(res.error) return setLoadError(res.message);
			setActivities(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error cargando las actividades. (LCL-1)');
		});
	};

	var getTypes = ()=>{
		API.getActivityTypes().then(res=>{
			if(res.error) return Toast.error(res.message);
			setTypes(res.data || []);
		}).catch(()=>{
			Toast.error('Hubo un error cargando los tipos de actividad. (LCL-1)');
		});
	};

	var openCreate = ()=>{
		setEditingId(null);
		setForm({ activity_type_id: null, title: '', description: '', outcome: '', date_activity: nowDatetimeInput(), completed: false });
		setPrompts(null);
		setShownModal(Modals.CREATE);
		getTypes();
	};

	var openInfo = (a: PromoterActivity)=>{
		setSelected(a);
		setShownModal(Modals.INFO);
	};

	var openEdit = (a: PromoterActivity)=>{
		setEditingId(a.activity_id);
		setForm({
			activity_type_id: a.activity_type_id,
			title: a.title,
			description: a.description || '',
			outcome: a.outcome || '',
			date_activity: formatUnix(a.date_activity, "yyyy-MM-dd'T'HH:mm"),
			completed: !!a.completed,
		});
		setPrompts(null);
		setShownModal(Modals.EDIT);
		getTypes();
	};

	var activeTypes = types || [];

	var create = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			activity_type_id: [{ rule: 'empty', label: 'Tipo de actividad' }],
			title: [{ rule: 'length', params: [2, 128], label: 'Título' }],
		});
		setPrompts(prompts);
		if(!valid) return;
		var date_activity = datetimeInputToUnix(form.date_activity);
		if(date_activity===null){ setPrompts(['La fecha no es válida']); return; }

		setLoading(true);
		API.createActivity(target, {
			activity_type_id: form.activity_type_id,
			title: form.title.trim(),
			description: form.description.trim() || null,
			outcome: form.outcome.trim() || null,
			date_activity,
			completed: form.completed,
		}).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Actividad agregada');
			var type = types?.find(t=>t.activity_type_id===form.activity_type_id);
			var created: PromoterActivity = {
				activity_id: res.data.activity_id,
				...target,
				activity_type_id: form.activity_type_id,
				type_name: type?.type_name || '',
				title: form.title.trim(),
				description: form.description.trim() || null,
				outcome: form.outcome.trim() || null,
				date_activity,
				completed: form.completed ? 1 : 0,
				date_completed: form.completed ? nowUnix() : null,
				user_creator: user?.admin_id ?? null,
				user_creator_name: user ? `${user.first_name} ${user.last_name}` : '',
				date_created: nowUnix(),
			};
			setActivities(prev=> [created, ...(prev || [])].sort((a, b)=> b.date_activity - a.date_activity));
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error agregando la actividad. (LCL-1)']);
		}).finally(()=>setLoading(false));
	};

	var saveEdit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			activity_type_id: [{ rule: 'empty', label: 'Tipo de actividad' }],
			title: [{ rule: 'length', params: [2, 128], label: 'Título' }],
		});
		setPrompts(prompts);
		if(!valid) return;
		var date_activity = datetimeInputToUnix(form.date_activity);
		if(date_activity===null){ setPrompts(['La fecha no es válida']); return; }

		setLoading(true);
		API.updateActivity(editingId, {
			activity_type_id: form.activity_type_id,
			title: form.title.trim(),
			description: form.description.trim() || null,
			outcome: form.outcome.trim() || null,
			date_activity,
		}).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Actividad actualizada');
			var type = types?.find(t=>t.activity_type_id===form.activity_type_id);
			setActivities(prev=> prev.map(x=> x.activity_id===editingId ? {
				...x,
				activity_type_id: form.activity_type_id,
				type_name: type?.type_name || x.type_name,
				title: form.title.trim(),
				description: form.description.trim() || null,
				outcome: form.outcome.trim() || null,
				date_activity,
			} : x).sort((a, b)=> b.date_activity - a.date_activity));
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error actualizando la actividad. (LCL-1)']);
		}).finally(()=>setLoading(false));
	};

	var complete = (a: PromoterActivity)=>{
		API.completeActivity(a.activity_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Actividad completada');
			setActivities(prev=> prev.map(x=> x.activity_id===a.activity_id ? { ...x, completed: 1, date_completed: nowUnix() } : x));
		}).catch(()=>{
			Toast.error('Hubo un error completando la actividad. (LCL-1)');
		});
	};

	var onDelete = (a: PromoterActivity)=>{
		setSelected(a);
		setPrompts(null);
		setShownModal(Modals.DELETE);
	};

	var submitDelete = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteActivity(selected.activity_id).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Actividad eliminada');
			setActivities(prev=> prev.filter(x=>x.activity_id!==selected.activity_id));
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error eliminando la actividad. (LCL-1)']);
		}).finally(()=>setLoading(false));
	};

	var setField = (k: keyof ActivityForm)=> (v: any)=> setForm(f=>({ ...f, [k]: v }));

	return <div className='record-section activities-section'>
		<div className='section-header'>
			<span>Actividades</span>
			{canEdit && <Button text='Agregar' iconName='plus' color='black' size='tiny' onClick={openCreate} />}
		</div>
		<div className='section-body'>
			<Table fitted striped>
				<Table.Head>
					<Table.Row>
						<Table.Cell header collapsing>Tipo</Table.Cell>
						<Table.Cell header>Título</Table.Cell>
						<Table.Cell header collapsing>Creador</Table.Cell>
						<Table.Cell header collapsing centered>Estado</Table.Cell>
						<Table.Cell header collapsing>Fecha</Table.Cell>
						<Table.Cell header collapsing centered><Icon name='wrench' /></Table.Cell>
					</Table.Row>
				</Table.Head>
				<Table.Body>
					{loadError ? (
						<Table.Row><Table.Cell colSpan={6} centered>{loadError}</Table.Cell></Table.Row>
					) : !activities ? (
						<Table.Row><Table.Cell colSpan={6} centered><Header loading text='Cargando datos' /></Table.Cell></Table.Row>
					) : activities.length===0 ? (
						<Table.Row><Table.Cell colSpan={6} centered>No hay actividades registradas</Table.Cell></Table.Row>
					) : activities.map(a=>(
						<Table.Row key={a.activity_id}>
							<Table.Cell collapsing>{a.type_name}</Table.Cell>
							<Table.Cell>
								<div className='act-title'>{a.title}</div>
							</Table.Cell>
							<Table.Cell collapsing>{a.user_creator_name || <Icon name='minus' />}</Table.Cell>
							<Table.Cell collapsing centered>
								{(()=>{ var st = activityStatus(a); return <Label color={st.color} size='small'>{st.text}</Label>; })()}
							</Table.Cell>
							<Table.Cell collapsing>{formatUnix(a.date_activity, 'dd/MMM/yyyy HH:mm')}</Table.Cell>
							<Table.Cell collapsing centered className='no-select'>
								<Button basic icon iconName='ellipsis' size='small' menu={[
									{ text: 'Info', iconName: 'circle-info', onClick: ()=>openInfo(a) },
									{ text: 'Completar', iconName: 'check', if: !a.completed && canEdit, onClick: ()=>complete(a) },
									{ text: 'Editar', iconName: 'pen', if: canEdit, onClick: ()=>openEdit(a) },
									{ text: 'Eliminar', iconName: 'trash', if: canEdit, onClick: ()=>onDelete(a) },
								]} />
							</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>
			</Table>
		</div>
		<Modal open={shownModal===Modals.CREATE || shownModal===Modals.EDIT} onClose={bindClick(setShownModal, null)} size="tiny">
			<Modal.Header text={shownModal===Modals.EDIT ? 'Editar actividad' : 'Agregar actividad'} />
			<Modal.Content style={{  maxWidth: '100%', margin: 'auto' }}>
				{!!form && <>
					<Dropdown
						label='Tipo de actividad'
						placeholder={'Selecciona un tipo'}
						value={form.activity_type_id!=null ? String(form.activity_type_id) : null}
						items={activeTypes.map(t=>({ text: t.type_name, value: String(t.activity_type_id) }))}
						onValueChange={v=>setForm(f=>({ ...f, activity_type_id: v!=null && v!=='' ? parseInt(v) : null }))}
					/>
					<Input fluid label='Título' placeholder='Título de la actividad' value={form.title} onChange={setField('title')} />
					<Input fluid label='Fecha y hora' inputType='datetime-local' value={form.date_activity} onChange={setField('date_activity')} />
					<Field>
						{shownModal!==Modals.EDIT && <Checkbox label='Marcar como completada' checked={form.completed} onChange={setField('completed')} />}
					</Field>
					<Input fluid label='Descripción' placeholder='Descripción (opcional)' textarea textareaRows={2} value={form.description} onChange={setField('description')} />
					<Input fluid label='Resultado' placeholder='Resultado (opcional)' textarea textareaRows={2} value={form.outcome} onChange={setField('outcome')} />
					<Message type='error' list={prompts} />
				</>}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={shownModal===Modals.EDIT ? saveEdit : create} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===Modals.INFO} onClose={bindClick(setShownModal, null)}>
			<Modal.Header text='Detalle de actividad' />
			<Modal.Content style={{ width: 500, maxWidth: '100%', margin: 'auto' }}>
				{!!selected && <Table fitted striped>
					<Table.Body>
						<Table.Row><Table.Cell collapsing><b>Tipo</b></Table.Cell><Table.Cell>{selected.type_name}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Título</b></Table.Cell><Table.Cell>{selected.title}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Estado</b></Table.Cell><Table.Cell>
							{(()=>{ var st = activityStatus(selected); return <Label color={st.color} size='small'>{st.text}</Label>; })()}
						</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Fecha</b></Table.Cell><Table.Cell>{formatUnix(selected.date_activity, 'dd/MMM/yyyy HH:mm')}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Creador</b></Table.Cell><Table.Cell>{selected.user_creator_name || <Icon name='minus' />}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Descripción</b></Table.Cell><Table.Cell>{selected.description || <Icon name='minus' />}</Table.Cell></Table.Row>
						<Table.Row><Table.Cell collapsing><b>Resultado</b></Table.Cell><Table.Cell>{selected.outcome || <Icon name='minus' />}</Table.Cell></Table.Row>
					</Table.Body>
				</Table>}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic size='small' onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal, null)}>
			<Modal.Header text='Eliminar actividad' />
			<Modal.Content style={{ width: 500, maxWidth: '100%', margin: 'auto' }}>
				{!!selected && <Header size='small' text='¿Eliminar actividad?' subtext={`Se eliminará "${selected.title}"`} />}
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Eliminar' color='red' size='small' onClick={submitDelete} />
			</Modal.Actions>
		</Modal>
	</div>;
}

export default Activities;
